import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, AUTH_HANDOFF_KEY, AUTH_HANDOFF_ERROR_KEY } from "../lib/auth";

/**
 * Handles the redirect from auth.emergentagent.com.
 * URL fragment will look like #session_id=XXXX.
 *
 * Triple-channel handoff (iOS-safe):
 *   • POPUP (desktop): postMessage to opener, then close.
 *   • NEW TAB (iOS/Android): write session_id to localStorage so the main tab
 *     can pick it up via the `storage` event or polling. Then close.
 *   • SAME-TAB fallback: exchange here directly and route home.
 */
export default function AuthCallback() {
  const { exchangeSessionId, continueAsGuest } = useAuth();
  const navigate = useNavigate();
  const handled = useRef(false);
  const [error, setError] = useState(null);
  const [guestBusy, setGuestBusy] = useState(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const isPopup = !!(window.opener && !window.opener.closed);

    if (!match) {
      // No session_id — either an error bounce from auth.emergentagent.com or
      // the user hit this page directly. Signal error through every channel.
      try {
        localStorage.setItem(
          AUTH_HANDOFF_ERROR_KEY,
          JSON.stringify({ error: "missing session", ts: Date.now() }),
        );
      } catch {}
      if (isPopup) {
        try {
          window.opener.postMessage({ type: "recce:auth:error", error: "missing session" }, "*");
        } catch {}
        setTimeout(() => window.close(), 150);
        return;
      }
      // Not a popup: show the retry UI (don't silently navigate — the user
      // was expecting to be signed in; stranding them home is confusing).
      setError("We couldn't complete sign-in. This can happen on iPhone Private Browsing — try regular Safari or continue as a guest.");
      return;
    }

    const sessionId = decodeURIComponent(match[1]);

    // Write to localStorage FIRST — this is the iOS-safe handoff channel.
    // Both popups and new tabs share localStorage with the opener (same origin).
    try {
      localStorage.setItem(
        AUTH_HANDOFF_KEY,
        JSON.stringify({ sessionId, ts: Date.now() }),
      );
    } catch {}

    // Popup / new-tab mode — also postMessage, then close ourselves.
    if (isPopup) {
      try {
        window.opener.postMessage({ type: "recce:auth:success", sessionId }, "*");
      } catch {}
      setTimeout(() => window.close(), 150);
      return;
    }

    // Same-tab (fallback) — exchange here.
    (async () => {
      try {
        await exchangeSessionId(sessionId);
        try { localStorage.removeItem(AUTH_HANDOFF_KEY); } catch {}
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Sign-in failed. Please try again or continue as a guest.");
      }
    })();
  }, [exchangeSessionId, navigate]);

  const retry = () => {
    window.location.href = "/";
  };

  const asGuest = async () => {
    setGuestBusy(true);
    try {
      await continueAsGuest();
      navigate("/", { replace: true });
    } catch {
      setGuestBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-8 bg-bg"
      data-testid="auth-callback"
    >
      <div className="text-center max-w-md">
        <p className="text-[11px] font-semibold uppercase tracking-[1.3px] text-amber">
          recce · auth
        </p>
        <h2 className="font-serif text-3xl mt-3">
          {error ? "Couldn't sign you in." : "One moment…"}
        </h2>
        {error ? (
          <>
            <p className="mt-4 text-ink-dim text-[14px] leading-relaxed">{error}</p>
            <div className="mt-6 flex flex-col items-center gap-2.5">
              <button
                onClick={retry}
                data-testid="auth-retry-btn"
                className="px-5 py-3 bg-amber text-[#20170a] rounded-full font-semibold text-[13px] hover:bg-amber-hover transition-colors"
              >
                try again
              </button>
              <button
                onClick={asGuest}
                disabled={guestBusy}
                data-testid="auth-callback-guest-btn"
                className="text-[13px] text-ink-dim hover:text-ink underline underline-offset-4 decoration-ink-ghost hover:decoration-amber transition-colors disabled:opacity-50"
              >
                {guestBusy ? "Entering as guest…" : "Continue as a guest"}
              </button>
            </div>
          </>
        ) : (
          <p className="mt-4 text-[11px] uppercase tracking-[0.14em] text-amber animate-pulse">
            ✦ exchanging session
          </p>
        )}
      </div>
    </div>
  );
}
